import { EventPanelFormType, EventPanelMetadata, PhoneCallOption } from '@mark43/cad-api';
import { useFormContext, useWatch } from 'react-hook-form';
import { phoneCallWithNormalizedPhoneCallDisplay } from '../../../phone-calls/phone-calls-store';
import type { PhoneCallView } from '../../../phone-calls/types';
import { usePhoneCallsStore } from '../../../phone-calls/use-phone-calls';
import { useEventMetadataWithTrackedKeys } from '../../event-panel-form/event-metadata';

export function getPhoneCallForEvent(
    phoneCalls: PhoneCallView[],
    phoneCallSelection: PhoneCallOption | null,
    eventMetadata?: EventPanelMetadata
): PhoneCallView | undefined {
    // For a cleared/historical event, just use the call that's saved to the event
    if (eventMetadata?.eventPhoneCall && !eventMetadata?.isActive) {
        return eventMetadata?.eventPhoneCall
            ? phoneCallWithNormalizedPhoneCallDisplay(eventMetadata?.eventPhoneCall)
            : undefined;
    }
    // if the event is active and is attached to a call, use the call from phone calls state
    if (eventMetadata?.eventPhoneCall && eventMetadata.isActive) {
        const matchedCallInState = phoneCalls.find(
            (phoneCall) => phoneCall.phoneNumberId === eventMetadata.eventPhoneCall?.phoneNumberId
        );
        return (
            matchedCallInState ??
            phoneCallWithNormalizedPhoneCallDisplay(eventMetadata.eventPhoneCall)
        );
    }
    // if the event is selecting a phone call, but has not yet saved the phone call, use the call from form state
    if (phoneCallSelection) {
        return phoneCalls.find(
            (phoneCall) => phoneCall.phoneNumberId === phoneCallSelection.phoneNumberId
        );
    }
    return undefined;
}

const observedKeys = ['eventPhoneCall', 'isActive'] as const;
export function usePhoneCallForEvent(): PhoneCallView | undefined {
    const eventForm = useFormContext<EventPanelFormType>();
    const phoneCalls = usePhoneCallsStore().activePhoneCallsList;
    const eventMetadata = useEventMetadataWithTrackedKeys(observedKeys);
    const phoneCallSelection = useWatch({
        control: eventForm.control,
        name: 'phoneCall',
    });
    return getPhoneCallForEvent(phoneCalls, phoneCallSelection, eventMetadata);
}
